// src/hooks/useStationen.js
// --- STATIONEN HOOKS ----------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from 'react';
import { db, withTimeout, supabase } from '../lib/supabase';
import { offeneZeilen } from '../lib/warteschlange';
import { useDebounced } from './useDebounced';

// Noch nicht gesendete Aenderungen (Warteschlange) ueber den Server-Stand
// legen. Ohne das zeigte ein Nachladen den ALTEN Stand - und die naechste
// Eingabe schrieb ihn womoeglich zurueck.
// Die letzten 10 Stationen auf dem Geraet behalten (vorher unbegrenzt - nach
// Wochen war der Geraetespeicher voll, und dann schlug das Merken von
// Eingaben fehl).
const STATION_CACHE_ANZAHL = 10;
// nurWennAelterAls (Zeitstempel): wie beim Protokoll-Zwischenspeicher. Das
// Vorab-Laden darf keinen neueren Stand ersetzen - sonst zeigte die Station
// ohne Netz wieder die alte Seriennummer (Pruefung 20.09.2026).
export function stationZwischenspeichern(stationId, daten, { nurWennAelterAls } = {}) {
  const LISTE = 'etb-station-cache-liste';
  if (nurWennAelterAls) {
    try {
      const vorhanden = JSON.parse(localStorage.getItem(STATION_CACHE + stationId) || 'null');
      if (vorhanden && (vorhanden.stand || 0) >= nurWennAelterAls) return;
    } catch { /* unlesbar - dann ersetzen */ }
  }
  try {
    let liste = [];
    try { liste = JSON.parse(localStorage.getItem(LISTE) || '[]'); } catch { liste = []; }
    if (!Array.isArray(liste)) liste = [];
    // Alte Staende ohne Liste (vor v57) einmalig einsammeln.
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STATION_CACHE) && k !== LISTE) {
        const id = k.slice(STATION_CACHE.length);
        if (!liste.includes(id)) liste.push(id);
      }
    }
    liste = [stationId, ...liste.filter(x => x !== stationId)];
    for (const alt of liste.slice(STATION_CACHE_ANZAHL)) localStorage.removeItem(STATION_CACHE + alt);
    liste = liste.slice(0, STATION_CACHE_ANZAHL);
    localStorage.setItem(LISTE, JSON.stringify(liste));
    localStorage.setItem(STATION_CACHE + stationId, JSON.stringify({ ...daten, stand: Date.now() }));
  } catch {
    try { localStorage.removeItem(STATION_CACHE + stationId); } catch { /* egal */ }
  }
}

// Alle Stammdaten einer Station (Station, Konfiguration, MS-Felder, Trafos,
// Wandler). ALLE Teile werden geprueft: Scheiterte z. B. das Laden der Trafos,
// zeigte das Formular sonst "0 Trafos" - und Speichern loeschte sie.
export async function stationDatenLaden(stationId) {
  const [s, c, f, t, w] = await Promise.all([
    withTimeout(db.stationen.get(stationId), 20000, 'Station laden'),
    withTimeout(db.stationKonfig.get(stationId), 20000, 'Konfiguration laden'),
    withTimeout(db.msFelder.listByStation(stationId), 20000, 'MS-Felder laden'),
    withTimeout(db.trafos.listByStation(stationId), 20000, 'Trafos laden'),
    withTimeout(db.wandler.listByStation(stationId), 20000, 'Wandler laden'),
  ]);
  if (s.error) throw s.error;
  if (c.error) throw new Error('Konfiguration: ' + c.error.message);
  if (f.error) throw new Error('MS-Felder: ' + f.error.message);
  if (t.error) throw new Error('Trafos: ' + t.error.message);
  if (w.error) throw new Error('Wandler: ' + w.error.message);
  // Name des Netzbetreibers (Netze-BW-Frage 04.10, Migration 53). Getrennt vom
  // Stationsobjekt - mitgeladene Felder duerfen nie zurueckgespeichert werden.
  // Scheitert es, bleibt der Name unbekannt (undefined) und nichts wird ausgeblendet.
  let vnbName = null;
  if (s.data?.vnb_id) {
    try {
      const v = await withTimeout(supabase.from('vnb').select('name').eq('id', s.data.vnb_id).maybeSingle(), 15000, 'Netzbetreiber laden');
      vnbName = v.error ? undefined : (v.data?.name || null);
    } catch { vnbName = undefined; }
  }
  return { station: s.data, config: c.data || null, msFelder: f.data || [], trafos: t.data || [],
           wandler: (w.data || []).filter(x => x.active !== false), vnbName };
}

function mitOffenen(liste, art, stationId) {
  const nachId = new Map((liste || []).map(x => [x.id, x]));
  for (const p of offeneZeilen(art)) {
    if (!p.id) continue;
    // Karten schicken seit v54 nur geaenderte Felder - ohne station_id. Dann
    // zaehlt, ob die Zeile zu dieser Station gehoert.
    if (p.station_id != null ? p.station_id !== stationId : !nachId.has(p.id)) continue;
    nachId.set(p.id, { ...(nachId.get(p.id) || {}), ...p });
  }
  return [...nachId.values()]
    .filter(x => x.active !== false)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

// Liste aller Stationen (für globale Suche)
export function useStationen({ search: rohSearch = '', pageSize = 100 } = {}) {
  // Siehe useKunden: erst suchen, wenn kurz nichts mehr getippt wird.
  const search = useDebounced(rohSearch, 350);
  const [stationen, setStationen] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  // Nur beim ALLERERSTEN Laden die Liste durch "laedt ..." ersetzen. Beim
  // Tippen bleibt das alte Ergebnis stehen, bis das neue da ist - sonst
  // blinkt die Liste auf dem Handy bei jedem Buchstaben weg und es fuehlt
  // sich an, als lade die Seite neu.
  const ersteLadung = useRef(true);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(0); }, [search, pageSize]);

  const load = useCallback(async (signal) => {
    if (ersteLadung.current) setLoading(true);
    setError(null);
    try {
      // Mit Timeout schützen, damit Tab-Wechsel keine Hänger erzeugt
      const queryPromise = db.stationen.listAll({
        search, limit: pageSize, offset: page * pageSize,
      });
      const timeoutPromise = new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout: Stationen laden')), 15000));
      const { data, count: total, error: err } = await Promise.race([queryPromise, timeoutPromise]);
      if (signal?.aborted) return;
      if (err) throw err;
      setStationen(data || []);
      setCount(total || 0);
    } catch (e) {
      if (signal?.aborted) return;
      setError(e.message || 'Fehler beim Laden der Stationen');
      setStationen([]);
    } finally {
      if (!signal?.aborted) { setLoading(false); ersteLadung.current = false; }
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  return {
    stationen,
    count,
    loading,
    error,
    page,
    setPage,
    totalPages: Math.max(1, Math.ceil(count / pageSize)),
    hasNext: page < Math.ceil(count / pageSize) - 1,
    hasPrev: page > 0,
    reload: load,
  };
}

// Einzelne Station mit komplettem Kontext (Konfiguration, MS-Felder, Trafos, Wandler)
const STATION_CACHE = 'etb-station-cache-';

export function useStation(stationId, { ausCache = false } = {}) {
  const [station, setStation] = useState(null);
  // Beim ERSTEN Laden darf die Seite "laedt ..." zeigen. Bei jedem weiteren
  // Nachladen NICHT mehr:
  //
  // Die Stammdaten im Protokoll speichern sich 800 ms nach der letzten Eingabe
  // selbst und rufen danach reload() auf. Solange dieses Nachladen loading auf
  // true setzte, ersetzte ProtokollFormPage die KOMPLETTE Seite durch
  // "Lade Protokoll ...". Alles wurde neu aufgebaut, die Tastatur klappte zu,
  // die Scrollposition war weg - bei jedem Buchstaben. Am Telefon unbenutzbar.
  const [config, setConfig] = useState(null);
  const [msFelder, setMsFelder] = useState([]);
  const [trafos, setTrafos] = useState([]);
  const [wandler, setWandler] = useState([]);
  const [vnbName, setVnbName] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ersteLadung = useRef(true);

  const load = useCallback(async () => {
    if (!stationId) {
      setStation(null);
      setConfig(null);
      setMsFelder([]);
      setTrafos([]);
      setWandler([]);
      setLoading(false);
      ersteLadung.current = false;
      return;
    }
    // Uebernahme mit Wartendem (Warteschlange) obendrauf - fuer Netz UND Cache.
    const uebernehmen = (d) => {
      const stationOffen = offeneZeilen('station').find(p => p.id === stationId);
      setStation(stationOffen ? { ...d.station, ...stationOffen } : d.station);
      const konfigOffen = offeneZeilen('konfig').find(p => p.station_id === stationId);
      setConfig(konfigOffen ? { ...(d.config || {}), ...konfigOffen } : (d.config || null));
      setMsFelder(mitOffenen(d.msFelder, 'msfeld', stationId));
      setTrafos(mitOffenen(d.trafos, 'trafo', stationId));
      setWandler(mitOffenen(d.wandler, 'wandler', stationId));
      setVnbName(d.vnbName);

    };
    if (ersteLadung.current && ausCache) {
      try {
        const gemerkt = JSON.parse(localStorage.getItem(STATION_CACHE + stationId) || 'null');
        if (gemerkt && gemerkt.station) {
          uebernehmen(gemerkt);
          setLoading(false);
          ersteLadung.current = false;
        }
      } catch { /* kein Cache */ }
    }
    if (ersteLadung.current) setLoading(true);
    setError(null);
    try {
      // Zeitgrenze je Teil: Vorher wartete die Protokollseite bei schlechtem
      // Netz bis zu 60 s auf die Stationsdaten ("es kam Lädt").
      const daten = await stationDatenLaden(stationId);
      uebernehmen(daten);
      if (ausCache) {
        stationZwischenspeichern(stationId, daten);
      }
    } catch (e) {
      setError(e.message || 'Fehler beim Laden der Station');
    } finally {
      setLoading(false);
      ersteLadung.current = false;
    }
  }, [stationId, ausCache]);

  // Bei einem Wechsel auf eine ANDERE Station darf wieder "laedt ..." kommen -
  // dort stehen ja noch gar keine Daten auf dem Bildschirm.
  useEffect(() => { ersteLadung.current = true; }, [stationId]);

  useEffect(() => { load(); }, [load]);

  // Helper: gruppierte Wandler nach Typ
  const stromwandler = wandler.filter(w => w.wandler_typ === 'strom');
  const spannungswandler = wandler.filter(w => w.wandler_typ === 'spannung');

  return {
    station,
    config,
    msFelder,
    trafos,
    wandler,
    vnbName,
    stromwandler,
    spannungswandler,
    loading,
    error,
    reload: load,
  };
}
